import React, { useState, useEffect } from "react";
import axios from "../../Axiosinstance";
import {
    TextField,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    Typography,
    Snackbar,
    IconButton,
    Tooltip
} from "@mui/material";
import { FaPlus } from "react-icons/fa";
import { BsPencilFill, BsTrashFill } from "react-icons/bs";
import Loader from '../../Loader';
import Header from '../Component/Header';

const CategoryList = () => {
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState("");
    const [createdTime, setCreatedTime] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [editingCategoryId, setEditingCategoryId] = useState(null);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("");

    useEffect(() => {
        fetchCategories();
    }, [page, rowsPerPage, searchTerm]);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await axios.get("/categories", {
                params: {
                    name: searchTerm,
                    page: page,
                    size: rowsPerPage,
                },
            });
            setCategories(response.data.content.reverse());
            setTotalItems(response.data.totalElements);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
        setLoading(false);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenModal = (category = null) => {
        if (category) {
            setNewCategoryName(category.name);
            setCreatedTime(category.createdTime);
            setEditingCategoryId(category.id);
        } else {
            setNewCategoryName("");
            setCreatedTime("");
            setEditingCategoryId(null);
        }
        setError("");
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleAddOrUpdateCategory = async () => {
        if (!newCategoryName || (editingCategoryId && !createdTime)) {
            setError("Both category name and created time are required when editing.");
            return;
        }

        setSaving(true);
        try {
            if (editingCategoryId) {
                await axios.put(`/categories/${editingCategoryId}`, {
                    name: newCategoryName,
                    createdTime: createdTime
                });
            } else {
                await axios.post("/categories", { name: newCategoryName });
            }
            fetchCategories();
            handleCloseModal();
        } catch (error) {
            console.error("Error saving category:", error);
            setError("Failed to save category. Please try again.");
        }
        setSaving(false);
    };

    const handleDeleteClick = (category) => {
        console.log("Category to delete:", category); // Debugging
        setCategoryToDelete(category);
        setOpenDeleteDialog(true);
    };
    
    const handleConfirmDelete = async () => {
        setOpenDeleteDialog(false); // Close the dialog first
        try {
            await axios.delete(`/categories/${categoryToDelete.id}`);
            fetchCategories(); // Refresh the list
            setToastMessage("Category deleted successfully");
        } catch (error) {
            // Check if the error status is 500 and customize the message
            if (error.response?.status === 500) {
                setToastMessage(
                    "Delete the courses associated with the category first, and then delete the category."
                );
            } else {
                setToastMessage("Failed to delete category. Please try again.");
            }
            console.error("Error deleting category:", error.response || error.message);
        }
        setToastOpen(true); // Show the toast with the appropriate message
    };
    
    

    const handleCloseToast = () => {
        setToastOpen(false);
    };

    return (
        <div style={{ marginTop: '24px', maxWidth: '1000px', margin: 'auto', padding: '16px' }}>
        <Header />
           <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                All Categories
            </Typography>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px', alignItems: 'center' }}>
           
                <div style={{ flexGrow: 1 }}>
                    <TextField
                        label="Search by name"
                        variant="outlined"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        fullWidth
                    />
                </div>
             
            <Button
                variant="contained"
                color="primary"
                sx={{
                    backgroundColor: '#1976d2',
                    '&:hover': {
                        backgroundColor: '#115293'
                    },
                    padding: '10px 20px',
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    
                }}
                onClick={() => handleOpenModal()}
                startIcon={<FaPlus />}
            >
                New Category
            </Button>
        
            </div>

            {loading ? (
                <Loader />
            ) : (
                <TableContainer component={Paper} sx={{ marginTop: 2 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Created Time</TableCell>
                                <TableCell>Course Count</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {categories.length > 0 ? (
                                categories.map((category, index) => (
                                    <TableRow key={category.id} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{category.name}</TableCell>
                                        <TableCell>{new Date(category.createdTime).toLocaleString()}</TableCell>
                                        <TableCell>{category.courseCount}</TableCell>
                                        <TableCell>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleOpenModal(category)}
                                                    sx={{ marginLeft: 1 }}
                                                >
                                                    <BsPencilFill />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    color="secondary"
                                                    onClick={() => handleDeleteClick(category)}
                                                    sx={{ marginLeft: 1 }}
                                                >
                                                    <BsTrashFill />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No categories found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TablePagination
                component="div"
                count={totalItems}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50]}
            />

            <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
                <DialogTitle>{editingCategoryId ? "Edit Category" : "Add Category"}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        label="Category Name"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                    />
                    {editingCategoryId && (
                        <TextField
                            label="Created Time"
                            value={createdTime}
                            onChange={(e) => setCreatedTime(e.target.value)}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            type="datetime-local"
                        />
                    )}
                    {error && <Typography sx={{ marginTop: 1, color: 'red' }}>{error}</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="error"  onClick={handleCloseModal} >
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleAddOrUpdateCategory}  disabled={saving}>
                        {saving ? <CircularProgress size={24} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
    <DialogTitle>Confirm Delete</DialogTitle>
    <DialogContent>
        <Typography>Are you sure you want to delete this category?</Typography>
    </DialogContent>
    <DialogActions>
        <Button variant="outlined" onClick={() => setOpenDeleteDialog(false)} color="primary">
            Cancel
        </Button>
        <Button
            variant="contained"
            onClick={handleConfirmDelete}
            color="error"
        >
            Confirm
        </Button>
    </DialogActions>
</Dialog>

            <Snackbar
                open={toastOpen}
                message={toastMessage}
                autoHideDuration={4000}
                onClose={handleCloseToast}
            />
        </div>
    );
};

export default CategoryList;
